import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import "@/lib/map-call-utils";


// Hide console logs to prevent showing in error overlay
// Store original console functions
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  info: console.info
};

// Expose original error logger on window so ErrorBoundary can log real errors
// without triggering the runtime error overlay
(window as any).__originalConsoleError = originalConsole.error.bind(console);

// Only log errors to console, filter out other messages
console.log = (...args) => {
  // Filter out the "Fetched points" messages and other similar logs that appear in the overlay
  if (
    (typeof args[0] === 'string' && args[0].includes('Fetched points')) ||
    (Array.isArray(args[0]) && args[0][0] === 'Fetched points')
  ) {
    return;
  }
  originalConsole.log(...args);
};

// Also filter warn and info messages that might appear in overlay
console.warn = (...args) => {
  if (typeof args[0] === 'string' && (
    args[0].includes('Fetched points') ||
    args[0].includes('[vite]')
  )) {
    return;
  }
  originalConsole.warn(...args);
};

// Suppress known React 18 compatibility warnings from react-draggable and
// Three.js so they don't trigger the runtime error overlay.
console.error = (...args) => {
  const msg = typeof args[0] === 'string' ? args[0] : '';
  if (
    msg.includes('Invalid hook call') ||
    msg.includes('findDOMNode') ||
    msg.includes('Context Lost') ||
    msg.includes('WebGLRenderer')
  ) {
    return; // swallow — these are harmless compatibility warnings
  }
  originalConsole.error(...args);
};

console.info = (...args) => {
  if (typeof args[0] === 'string' && (
    args[0].includes('Fetched points') ||
    args[0].includes('[vite]')
  )) {
    return;
  }
  originalConsole.info(...args);
};

// Suppress known Leaflet animation errors that fire during React hot-module
// reload. Leaflet's zoom/pan animation callbacks hold stale references to DOM
// nodes that React has already removed, causing "_leaflet_pos" TypeError.
// These are harmless false-positives and must not crash the app.
// Use capture phase (3rd arg = true) so this fires BEFORE the Replit
// runtime error overlay's own window.addEventListener("error") handler.
// stopImmediatePropagation() prevents the overlay from ever seeing the event.
window.addEventListener('error', (event) => {
  const msg = event.message ?? '';
  if (
    msg.includes('_leaflet_pos') ||
    (msg.includes('leaflet') && msg.includes('undefined')) ||
    msg.includes('Invalid hook call') ||
    msg.includes('findDOMNode') ||
    msg.includes('Context Lost') ||
    msg.includes('WebGLRenderer')
  ) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return false;
  }
}, true); // <-- capture phase

window.addEventListener('unhandledrejection', (event) => {
  const msg = String(event.reason?.message ?? event.reason ?? '');
  if (
    msg.includes('_leaflet_pos') ||
    msg.includes('Invalid hook call') ||
    msg.includes('findDOMNode')
  ) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true); // <-- capture phase

createRoot(document.getElementById("root")!).render(<App />);
